import BigNumber from 'bignumber.js';
import { ActionCableService, sharePublishReplay, swalloError } from 'ngx-libs';
import { combineLatest, forkJoin, Observable, timer } from 'rxjs';
import { debounceTime, exhaustMap, map, shareReplay, startWith, tap } from 'rxjs/operators';
import { keepUpdater, unsubscribeLater } from '../rxjs';
import ConfigAbi from 'src/app/abi/config';
import { AccountService } from '../services/account.service';
import { ContractService } from '../services/contract.service';
import { EthWalletService } from '../services/eth-wallet.service';
import { MainService } from '../services/main.service';
import { Base } from './base';
import { Market } from './market';

export class Config extends Base {
  // pools: Observable<string[]>;
  all: Observable<any>;
  poolReservedRate: Observable<BigNumber>;

  constructor(
    protected contract: string,
    protected accountService: AccountService,
    protected contractService: ContractService,
    private ethWalletService: EthWalletService,
    private actionCableService: ActionCableService,
    private mainService: MainService,
    private market: Market
  ) {
    super(contract, ConfigAbi, accountService, contractService);

    const received = this.actionCableService.received({
      channel: 'ConfigChannel'
    }).pipe(
      startWith(0),
      debounceTime(10)
    );

    // this.all = combineLatest([received, timer(0, 60000)]).pipe(
    //   debounceTime(20),
    //   keepUpdater(),
    //   exhaustMap(() => this.getAllOffChain().pipe(
    //     swalloError(),
    //     unsubscribeLater(5000),
    //     shareReplay(1)
    //   )),
    //   tap((config) => {
    //     this.market.contractVault.config.initialMarginRiskRate = config.initialMarginRiskRate.toDecimal();
    //     this.market.contractVault.config.riskFreeRate = config.riskFreeRate.toDecimal();
    //     this.market.contractVault.config.priceRatio = config.priceRatio.toDecimal();
    //     this.market.contractVault.config.priceRatio2 = config.priceRatio2.toDecimal();
    //     this.market.contractVault.config.priceRatioUtilization = config.priceRatioUtilization.toDecimal();
    //     this.market.contractVault.config.spotFee = config.spotFee.toDecimal();
    //     this.market.contractVault.config.optionFee = config.optionFee.toDecimal();
    //     this.market.contractVault.config.minPremium = config.minPremium.toDecimal();
    //     this.market.contractVault.config.exerciseFeeRate = config.exerciseFeeRate.toDecimal();
    //     this.market.contractVault.config.profitFeeRate = config.profitFeeRate.toDecimal();
    //     this.market.contractVault.config.poolReservedRate = config.poolReservedRate.toDecimal();
    //   }),
    //   sharePublishReplay(1)
    // );

    this.all = combineLatest([received, timer(0, 60000)]).pipe(
      debounceTime(20),
      exhaustMap(() => this.getAllOffChain().pipe(
        swalloError()
      )),
      tap((config) => {
        this.market.contractVault.config.initialMarginRiskRate = config.initialMarginRiskRate.toDecimal();
        this.market.contractVault.config.riskFreeRate = config.riskFreeRate.toDecimal();
        this.market.contractVault.config.priceRatio = config.priceRatio.toDecimal();
        this.market.contractVault.config.priceRatio2 = config.priceRatio2.toDecimal();
        this.market.contractVault.config.priceRatioUtilization = config.priceRatioUtilization.toDecimal();
        this.market.contractVault.config.spotFee = config.spotFee.toDecimal();
        this.market.contractVault.config.optionFee = config.optionFee.toDecimal();
        this.market.contractVault.config.minPremium = config.minPremium.toDecimal();
        this.market.contractVault.config.exerciseFeeRate = config.exerciseFeeRate.toDecimal();
        this.market.contractVault.config.profitFeeRate = config.profitFeeRate.toDecimal();
        this.market.contractVault.config.poolReservedRate = config.poolReservedRate.toDecimal();
      }),
      shareReplay(1)
    );

    this.poolReservedRate = this.all.pipe(
      map((a) => a.poolReservedRate)
    );
  }

  getAllOffChain() {
    return this.mainService.markets.of(this.market.name).config.query().pipe(
      map((json) => json.config)
    );
  }

  getAll() {
    return forkJoin([
      this.getInitialMarginRiskRate(),
      this.getRiskFreeRate(),
      this.getPriceRatio(),
      this.getPriceRatio2(),
      this.getPriceRatioUtilization(),
      this.getSpotFee(),
      this.getOptionFee(),
      this.getMinPremium(),
      this.getExerciseFeeRate(),
      this.getProfitFeeRate()
    ]).pipe(
      map(
        ([
          initialMarginRiskRate,
          riskFreeRate,
          priceRatio,
          priceRatio2,
          priceRatioUtilization,
          spotFee,
          optionFee,
          minPremium,
          exerciseFeeRate,
          profitFeeRate
        ]) => {
          return {
            initialMarginRiskRate,
            riskFreeRate,
            priceRatio,
            priceRatio2,
            priceRatioUtilization,
            spotFee,
            optionFee,
            minPremium,
            exerciseFeeRate,
            profitFeeRate
          };
        }
      )
    );
  }

  getInitialMarginRiskRate() {
    return this.call((i) => i.methods.initialMarginRiskRate()).pipe(map((v) => new BigNumber(v).fromDecimal()));
  }

  // getLiquidateRate() {
  //   return this.call((i) => i.methods.liquidateRate());
  // }

  // getClearRate() {
  //   return this.call((i) => i.methods.clearRate());
  // }

  // getLiquidationReward() {
  //   return this.call((i) => i.methods.liquidationReward());
  // }

  getRiskFreeRate() {
    return this.call((i) => i.methods.riskFreeRate()).pipe(map((v) => new BigNumber(v).fromDecimal()));
  }

  getPriceRatio() {
    return this.call((i) => i.methods.priceRatio()).pipe(map((v) => new BigNumber(v).fromDecimal()));
  }

  getPriceRatio2() {
    return this.call((i) => i.methods.priceRatio2()).pipe(map((v) => new BigNumber(v).fromDecimal()));
  }

  getPriceRatioUtilization() {
    return this.call((i) => i.methods.priceRatioUtilization()).pipe(map((v) => new BigNumber(v).fromDecimal()));
  }

  getSpotFee() {
    return this.call((i) => i.methods.spotFee()).pipe(map((v) => new BigNumber(v).fromDecimal()));
  }

  getOptionFee() {
    return this.call((i) => i.methods.optionFee()).pipe(map((v) => new BigNumber(v).fromDecimal()));
  }

  getMinPremium() {
    return this.call((i) => i.methods.minPremium()).pipe(map((v) => new BigNumber(v).fromDecimal()));
  }

  getExerciseFeeRate() {
    return this.call((i) => i.methods.exerciseFeeRate()).pipe(map((v) => new BigNumber(v).fromDecimal()));
  }

  getProfitFeeRate() {
    return this.call((i) => i.methods.profitFeeRate()).pipe(map((v) => new BigNumber(v).fromDecimal()));
  }
}
